import React, { useState, useMemo } from "react";
import {
  Calendar,
  TrendingUp,
  Eye,
  Users,
  Target,
  MousePointerClick,
  Radio,
  Clock,
} from "lucide-react";
import { ClientLogo } from "../../components/ClientLogo";
import type {
  DMASummary,
  NetworkSummary,
  SegmentSummary,
  StreamingReportResponse,
} from "../../types";
import { MetricCard } from "../../components/MetricCard";
import { BarChart } from "../../components/BarChart";
import { SectionToggleWrapper } from "../../components/SectionToggleWrapper";
import { CARD_BG, PAGE_BG, SHADOW } from "../../config/theme";
import { formatReportDateLabel } from "../../utils/dateFormatting";

interface StreamingLayoutProps {
  data: StreamingReportResponse;
  reportId?: string;
  clientId?: string;
}

type SortDirection = "asc" | "desc";
type SortValue = number | string;
type SortState<Field extends string> = {
  field: Field;
  direction: SortDirection;
};
type SortAccessors<Row, Field extends string> = Record<
  Field,
  (row: Row) => SortValue
>;
type SegmentSortField = "segment" | "conversions" | "visits" | "spend";
type DmaSortField = "dma" | "visits";
type NetworkSortField = "network" | "visits";

const TOP_LINE_LIMIT = 5;

const segmentSortAccessors: SortAccessors<SegmentSummary, SegmentSortField> = {
  segment: (segment) => segment.segment_name,
  conversions: (segment) => segment.total_conversions,
  visits: (segment) => segment.total_verified_visits,
  spend: (segment) => parseFloat(segment.total_spend),
};

const dmaSortAccessors: SortAccessors<DMASummary, DmaSortField> = {
  dma: (dma) => dma.dma_name,
  visits: (dma) => dma.total_verified_visits,
};

const networkSortAccessors: SortAccessors<NetworkSummary, NetworkSortField> = {
  network: (network) => network.network_name,
  visits: (network) => network.total_verified_visits,
};

function nextSortState<Field extends string>(
  current: SortState<Field>,
  field: Field,
): SortState<Field> {
  if (current.field === field) {
    return {
      field,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }

  return { field, direction: "desc" };
}

function compareSortValues(
  aValue: SortValue,
  bValue: SortValue,
  direction: SortDirection,
): number {
  if (typeof aValue === "string" && typeof bValue === "string") {
    return direction === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  }

  return direction === "asc"
    ? Number(aValue) - Number(bValue)
    : Number(bValue) - Number(aValue);
}

function sortRows<Row, Field extends string>(
  rows: Row[],
  sortState: SortState<Field>,
  accessors: SortAccessors<Row, Field>,
): Row[] {
  const accessor = accessors[sortState.field];
  return [...rows].sort((a, b) =>
    compareSortValues(accessor(a), accessor(b), sortState.direction),
  );
}

function topNBy<Row>(
  rows: Row[],
  valueFor: (row: Row) => number,
  limit = TOP_LINE_LIMIT,
): Row[] {
  return rows
    .filter((row) => valueFor(row) > 0)
    .sort((a, b) => valueFor(b) - valueFor(a))
    .slice(0, limit);
}

const parseDateOnlyString = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsedDate = new Date(year, monthIndex, day);

  if (
    Number.isNaN(parsedDate.getTime()) ||
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== monthIndex ||
    parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
};

/**
 * Dedicated layout for MNTN Streaming TV advertising reports.
 * Displays segment, DMA, and network performance with reach, frequency,
 * conversion metrics, and daily trends.
 */
export const StreamingLayout: React.FC<StreamingLayoutProps> = ({
  data,
  clientId,
}) => {
  const { summary, segments, dmas, networks, daily_metrics } = data;
  const resolvedClientId = clientId ?? "ajinomoto";

  const [hiddenSections, setHiddenSections] = useState<Record<string, boolean>>(
    {},
  );

  const hideSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: true }));

  const restoreSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: false }));

  const isSectionHidden = (sectionId: string) =>
    Boolean(hiddenSections[sectionId]);

  const formattedDateRange = useMemo(() => {
    const startRaw = summary.date_range_start;
    const endRaw = summary.date_range_end;
    if (!startRaw || !endRaw) {
      return `${startRaw} to ${endRaw}`;
    }

    const startDate = parseDateOnlyString(startRaw);
    const endDate = parseDateOnlyString(endRaw);
    if (!startDate || !endDate) {
      return `${startRaw} to ${endRaw}`;
    }

    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${formatter.format(startDate)} to ${formatter.format(endDate)}`;
  }, [summary.date_range_start, summary.date_range_end]);


  const [segmentSort, setSegmentSort] = useState<SortState<SegmentSortField>>({
    field: "conversions",
    direction: "desc",
  });
  const [dmaSort, setDmaSort] = useState<SortState<DmaSortField>>({
    field: "visits",
    direction: "desc",
  });
  const [networkSort, setNetworkSort] = useState<SortState<NetworkSortField>>({
    field: "visits",
    direction: "desc",
  });

  const handleSegmentSort = (field: SegmentSortField) =>
    setSegmentSort((current) => nextSortState(current, field));

  const handleDmaSort = (field: DmaSortField) =>
    setDmaSort((current) => nextSortState(current, field));

  const handleNetworkSort = (field: NetworkSortField) =>
    setNetworkSort((current) => nextSortState(current, field));

  const sortedSegments = sortRows(segments, segmentSort, segmentSortAccessors);
  const sortedDmas = sortRows(dmas, dmaSort, dmaSortAccessors);
  const sortedNetworks = sortRows(networks, networkSort, networkSortAccessors);

  const topSegmentsByVerifiedVisits = topNBy(
    segments,
    (segment) => segment.total_verified_visits,
  );
  const topSegmentsByReach = topNBy(
    segments,
    (segment) => segment.households_reached,
  );
  const topDmasByVisits = topNBy(dmas, (dma) => dma.total_verified_visits);
  const topDmasByReach = topNBy(dmas, (dma) => dma.households_reached ?? 0);
  const topNetworksByVisits = topNBy(
    networks,
    (network) => network.total_verified_visits,
  );

  // Prepare dataset for top networks bar chart
  const networkBarData = {
    labels: topNetworksByVisits.map((n) => n.network_name),
    datasets: [
      {
        label: "Verified Visits",
        data: topNetworksByVisits.map((n) => n.total_verified_visits),
        backgroundColor: "rgba(139, 92, 246, 0.8)",
      },
    ],
  };

  const formatVisitsValue = (value: number): string =>
    new Intl.NumberFormat("en-US").format(value);

  // Prepare daily trend data
  const dailyTrendData = {
    labels: daily_metrics.map((d) => formatReportDateLabel(d.report_date)),
    datasets: [
      {
        label: "Impressions",
        data: daily_metrics.map((d) => d.impressions),
        borderColor: "rgba(59, 130, 246, 1)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        yAxisID: "y",
      },
      {
        label: "Conversions",
        data: daily_metrics.map((d) => d.total_conversions),
        borderColor: "rgba(16, 185, 129, 1)",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        yAxisID: "y1",
      },
    ],
  };

  return (
    <div className="py-16 px-8" data-dashboard>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                MNTN Streaming TV Performance Report
              </h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-[#B5A9AA]">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  Streaming TV
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formattedDateRange}
                </span>
                <span className="flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  {summary.segment_count} segments
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {summary.total_days} days
                </span>
              </div>
            </div>
            <div>
                <ClientLogo
                  clientId={resolvedClientId}
                className="h-16 object-contain"
                fallback={
                  <div className="h-16 w-16 bg-gray-200 dark:bg-[#5B5353] rounded" />
                }
              />
            </div>
          </div>
        </div>

        {/* Summary Metrics */}
        <SectionToggleWrapper
          sectionId="streaming-summary"
          title="Campaign Performance Overview"
          hidden={isSectionHidden("streaming-summary")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Campaign Performance Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <MetricCard
              title="Total Impressions"
              value={summary.total_impressions.toLocaleString()}
              icon={<Eye className="w-5 h-5" />}
              subtitle={`${summary.segment_count} segments`}
            />
            <MetricCard
              title="Households Reached"
              value={summary.total_households_reached.toLocaleString()}
              icon={<Users className="w-5 h-5" />}
              subtitle={`${Number(summary.average_frequency).toFixed(2)}x avg frequency`}
            />
            <MetricCard
              title="Verified Visits"
              value={summary.total_verified_visits.toLocaleString()}
              icon={<MousePointerClick className="w-5 h-5" />}
              subtitle={`${dmas.length} markets`}
            />
            <MetricCard
              title="Total Conversions"
              value={summary.total_conversions.toLocaleString()}
              icon={<Target className="w-5 h-5" />}
              subtitle={`${networks.length} networks`}
            />
          </div>
        </SectionToggleWrapper>

        {/* Daily Trend Chart */}
        {daily_metrics.length > 0 && (
          <SectionToggleWrapper
            sectionId="streaming-daily-trends"
            title="Daily Performance Trends"
            hidden={isSectionHidden("streaming-daily-trends")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
              >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Daily Performance Trends
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Impressions and conversions over {summary.total_days} days
              </p>
              <BarChart
                labels={dailyTrendData.labels}
                datasets={dailyTrendData.datasets}
                title=""
                stacked={false}
                showLegend={true}
                horizontal={false}
              />
            </div>
          </SectionToggleWrapper>
        )}

        {/* Segment Performance Charts */}
        <SectionToggleWrapper
          sectionId="streaming-dimension-performance"
          title="Performance by Dimension"
          hidden={isSectionHidden("streaming-dimension-performance")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Performance by Dimension
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Segments by Verified Visits */}
            {topSegmentsByVerifiedVisits.length > 0 && (
              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  Top Segments by Verified Visits
                </h3>
                <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                  Top {TOP_LINE_LIMIT} audience segments by verified visit volume
                </p>
                <BarChart
                  labels={topSegmentsByVerifiedVisits.map(
                    (segment) => segment.segment_name,
                  )}
                  datasets={[
                    {
                      label: "Verified Visits",
                      data: topSegmentsByVerifiedVisits.map(
                        (segment) => segment.total_verified_visits,
                      ),
                      backgroundColor: "rgba(59, 130, 246, 0.8)",
                    },
                  ]}
                  title=""
                  horizontal={true}
                  showLegend={false}
                  showDataLabels={true}
                  valueFormatter={(value) => value.toLocaleString()}
                />
              </div>
            )}

            {/* Top Segments by Reach */}
            {topSegmentsByReach.length > 0 && (
              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  Top Segments by Reach
                </h3>
                <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                  Top {TOP_LINE_LIMIT} audience segments by households reached
                </p>
                <BarChart
                  labels={topSegmentsByReach.map((segment) => segment.segment_name)}
                  datasets={[
                    {
                      label: "Households Reached",
                      data: topSegmentsByReach.map(
                        (segment) => segment.households_reached,
                      ),
                      backgroundColor: "rgba(139, 92, 246, 0.8)",
                    },
                  ]}
                  title=""
                  horizontal={true}
                  showLegend={false}
                  showDataLabels={true}
                  valueFormatter={(value) => value.toLocaleString()}
                />
              </div>
            )}

            {/* Top Markets by Verified Visits */}
            {topDmasByVisits.length > 0 && (
              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  Top Markets by Verified Visits
                </h3>
                <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                  Top {TOP_LINE_LIMIT} performing markets (DMA)
                </p>
                <BarChart
                  labels={topDmasByVisits.map((d) => d.dma_name)}
                  datasets={[
                    {
                      label: "Verified Visits",
                      data: topDmasByVisits.map((d) => d.total_verified_visits),
                      backgroundColor: "rgba(16, 185, 129, 0.8)",
                    },
                  ]}
                  title=""
                  horizontal={true}
                  showLegend={false}
                  showDataLabels={true}
                  valueFormatter={(value) => value.toLocaleString()}
                />
              </div>
            )}

            {/* Top Markets by Reach */}
            {topDmasByReach.length > 0 && (
              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  Top Markets by Reach
                </h3>
                <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                  Top {TOP_LINE_LIMIT} markets by households reached
                </p>
                <BarChart
                  labels={topDmasByReach.map((d) => d.dma_name)}
                  datasets={[
                    {
                      label: "Households Reached",
                      data: topDmasByReach.map((d) => d.households_reached ?? 0),
                      backgroundColor: "rgba(139, 92, 246, 0.8)",
                    },
                  ]}
                  title=""
                  horizontal={true}
                  showLegend={false}
                  showDataLabels={true}
                  valueFormatter={(value) => value.toLocaleString()}
                />
              </div>
            )}
          </div>
        </SectionToggleWrapper>

        {/* Network Visits Chart */}
        {topNetworksByVisits.length > 0 && (
          <SectionToggleWrapper
            sectionId="streaming-network-visits"
            title="Top Networks by Verified Visits"
            hidden={isSectionHidden("streaming-network-visits")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div
              className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6 pb-10`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <Radio className="w-5 h-5" />
                Top Networks by Verified Visits
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
                Top {TOP_LINE_LIMIT} TV networks by visit volume
              </p>
              <BarChart
                labels={networkBarData.labels}
                datasets={networkBarData.datasets}
                title=""
                horizontal={false}
                showLegend={false}
                showDataLabels={true}
                valueFormatter={formatVisitsValue}
              />
            </div>
          </SectionToggleWrapper>
        )}

        {/* Segment Performance Table */}
        <SectionToggleWrapper
          sectionId="streaming-segment-table"
          title="Segment Performance"
          hidden={isSectionHidden("streaming-segment-table")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Segment Performance ({segments.length})
            </h3>
            <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
              Audience segments ranked by verified visits and store locator
              conversions
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-[#5B5353]">
                    <th
                      className="text-left p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleSegmentSort("segment")}
                    >
                      Segment{" "}
                      {segmentSort.field === "segment" &&
                        (segmentSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleSegmentSort("conversions")}
                    >
                      Store Locator Conversions{" "}
                      {segmentSort.field === "conversions" &&
                        (segmentSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleSegmentSort("visits")}
                    >
                      Verified Visits{" "}
                      {segmentSort.field === "visits" &&
                        (segmentSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleSegmentSort("spend")}
                    >
                      Spend{" "}
                      {segmentSort.field === "spend" &&
                        (segmentSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Visit Rate
                    </th>
                    <th className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Store Locator Click Rate
                    </th>
                    <th className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Cost / Visit
                    </th>
                    <th className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Impressions
                    </th>
                    <th className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Households
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSegments.slice(0, 20).map((segment, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-100 dark:border-[#5B5353] hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="p-3 text-gray-900 dark:text-white">
                        <div className="font-medium">
                          {segment.segment_name}
                        </div>
                      </td>
                      <td className="p-3 text-right text-gray-900 dark:text-white font-semibold">
                        {segment.total_conversions.toLocaleString()}
                      </td>
                      <td className="p-3 text-right text-gray-900 dark:text-white">
                        {segment.total_verified_visits.toLocaleString()}
                      </td>
                      <td className="p-3 text-right text-gray-900 dark:text-white">
                        {Number(segment.total_spend).toLocaleString(undefined, {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="p-3 text-right text-gray-500 dark:text-[#B5A9AA]">
                        {parseFloat(segment.visit_rate).toFixed(2)}%
                      </td>
                      <td className="p-3 text-right text-gray-500 dark:text-[#B5A9AA]">
                        {parseFloat(segment.conversion_rate).toFixed(2)}%
                      </td>
                      <td className="p-3 text-right text-gray-500 dark:text-[#B5A9AA]">
                        {Number(segment.cost_per_visit).toLocaleString(
                          undefined,
                          {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          },
                        )}
                      </td>
                      <td className="p-3 text-right text-gray-500 dark:text-[#B5A9AA]">
                        {segment.impressions.toLocaleString()}
                      </td>
                      <td className="p-3 text-right text-gray-500 dark:text-[#B5A9AA]">
                        {segment.households_reached.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {segments.length > 20 && (
              <p className="text-sm text-gray-500 dark:text-[#B5A9AA] mt-4 text-center">
                Showing top 20 of {segments.length} segments
              </p>
            )}
          </div>
        </SectionToggleWrapper>

        {/* DMA Performance Table */}
        <SectionToggleWrapper
          sectionId="streaming-dma-table"
          title="Market (DMA) Performance"
          hidden={isSectionHidden("streaming-dma-table")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Market (DMA) Performance ({dmas.length})
            </h3>
            <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
              Detailed market metrics sorted by verified visits
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-[#5B5353]">
                    <th
                      className="text-left p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleDmaSort("dma")}
                    >
                      Market (DMA){" "}
                      {dmaSort.field === "dma" &&
                        (dmaSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleDmaSort("visits")}
                    >
                      Verified Visits{" "}
                      {dmaSort.field === "visits" &&
                        (dmaSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDmas.slice(0, 20).map((dma, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-100 dark:border-[#5B5353] hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="p-3 text-gray-900 dark:text-[#FFF8F9] font-medium">
                        {dma.dma_name}
                      </td>
                      <td className="p-3 text-right text-gray-900 dark:text-white font-semibold">
                        {dma.total_verified_visits.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {dmas.length > 20 && (
              <p className="text-sm text-gray-500 dark:text-[#B5A9AA] mt-4 text-center">
                Showing top 20 of {dmas.length} markets
              </p>
            )}
          </div>
        </SectionToggleWrapper>

        {/* Network Performance Table */}
        <SectionToggleWrapper
          sectionId="streaming-network-table"
          title="Network Performance"
          hidden={isSectionHidden("streaming-network-table")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Network Performance ({networks.length})
            </h3>
            <p className="text-sm text-gray-600 dark:text-[#B5A9AA] mb-4">
              Detailed TV network metrics sorted by verified visits
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-[#5B5353]">
                    <th
                      className="text-left p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleNetworkSort("network")}
                    >
                      TV Network{" "}
                      {networkSort.field === "network" &&
                        (networkSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="text-right p-3 text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleNetworkSort("visits")}
                    >
                      Verified Visits{" "}
                      {networkSort.field === "visits" &&
                        (networkSort.direction === "asc" ? "↑" : "↓")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedNetworks.slice(0, 20).map((network, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-100 dark:border-[#5B5353] hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="p-3 text-gray-900 dark:text-[#FFF8F9] font-medium">
                        {network.network_name}
                      </td>
                      <td className="p-3 text-right text-gray-900 dark:text-white font-semibold">
                        {network.total_verified_visits.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {networks.length > 20 && (
              <p className="text-sm text-gray-500 dark:text-[#B5A9AA] mt-4 text-center">
                Showing top 20 of {networks.length} networks
              </p>
            )}
          </div>
        </SectionToggleWrapper>
      </div>
    </div>
  );
};
